import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { FinancialService } from "../../services/financial.service";
import { MessageService } from "../../services/message.service";
import { Router } from "@angular/router";
import { CommonService } from "../../services/common.service";

interface RawData {
  project_id: number;
  project_name: string;
  cost_head: string;
  total_budget: number;
  intended_spend: number;
  actual_spend: number;
  ougdp_details: Array<{
    vendor_id: number;
    vendor_name: string;
    total_po_amount: number;
    vendor_count: number;
    spend_amount: number;
  }>;
}

interface Project {
  id: number;
  name: string;
  values: {
    [costHead: string]: {
      budget: number;
      spend: number;
      intended_spend: number;
      ougdpData: any;
    };
  };
}

@Component({
  selector: "app-finance-comparitor",
  templateUrl: "./finance-comparitor.component.html",
  styleUrls: ["./finance-comparitor.component.scss"],
})
export class FinanceComparitorComponent implements OnInit, OnChanges {
  rawData: RawData[] = [];
  projects: Project[] = [];
  costHeads: string[] = [];
  isLoading: boolean = false;

  // Vendor colors for chart logic inside the popover
  private vendorColors = [
    "#3498db",
    "#e74c3c",
    "#2ecc71",
    "#f39c12",
    "#9b59b6",
    "#1abc9c",
    "#34495e",
    "#e67e22",
    "#95a5a6",
    "#f1c40f",
  ];

  @Input() tagIds: any = [];
  @Input() projectId: number | null | undefined = null;
  @Input() portfolioId: number | null | undefined = null;
  @Input() subportfolioId: number | null | undefined = null;
  @Input() programId: number | null | undefined = null;
  @Input() vendorId: number | null | undefined = null;
  displayLabels: any;

  constructor(
    private readonly finacialService: FinancialService,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.displayLabels = this.commonService.getLabels();
    this.getComparitorData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tagIds"] && !changes["tagIds"].firstChange) {
      this.getComparitorData();
    }
  }

  getRequestParams(): string {
    let params: Array<string> = [];
    if (this.projectId) params.push(`project_id=${this.projectId}`);
    else if (this.programId) params.push(`program_id=${this.programId}`);
    else if (this.subportfolioId)
      params.push(`subportfolio_id=${this.subportfolioId}`);
    else if (this.portfolioId) params.push(`portfolio_id=${this.portfolioId}`);
    else if (this.vendorId) params.push(`vendor_id=${this.vendorId}`);
    else if (this.tagIds && this.tagIds.length > 0) {
      params.push(`tags_ids=${this.tagIds.join(",")}`);
    }
    return params.join("&");
  }

  getComparitorData() {
    const queryParams = this.getRequestParams();
    this.isLoading = true;
    this.finacialService
      .getBugetsInsigtht(queryParams)
      .subscribe((response: any) => {
        this.rawData = response;
        this.isLoading = false;
        this.prepareData();
      });
  }

  prepareData() {
    const projectsMap: { [id: number]: Project } = {};
    const heads = new Set<string>();

    this.rawData.forEach((d) => {
      if (!projectsMap[d.project_id]) {
        projectsMap[d.project_id] = {
          id: d.project_id,
          name: d.project_name,
          values: {},
        };
      }
      projectsMap[d.project_id].values[d.cost_head] = {
        budget: d.total_budget,
        spend: d.actual_spend,
        ougdpData: d.ougdp_details,
        intended_spend: d.intended_spend,
      };
      heads.add(d.cost_head);
    });

    this.projects = Object.values(projectsMap);
    this.costHeads = Array.from(heads);
    this.setGnews();
  }

  // Formatting & Utility Methods
  utilization(spend: number, budget: number): number {
    if (budget <= 0) return spend > 0 ? 2 : 0;
    return spend / budget;
  }

  color(util: number): string {
    if (util > 1) return "linear-gradient(45deg, #DD2C54, #f70029a5)";
    return "linear-gradient(0deg, #41a064ff, #5edd8dff)";
  }

  fmtShort(n: number): string {
    if (!n) return "0";
    if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }

  // Row Total Calculations
  getTotalSpend(p: Project): number {
    return this.costHeads.reduce(
      (acc, h) => acc + (p.values[h]?.spend || 0),
      0
    );
  }

  getTotalBudget(p: Project): number {
    return this.costHeads.reduce(
      (acc, h) => acc + (p.values[h]?.budget || 0),
      0
    );
  }

  getTotalIntendedSpend(p: Project): number {
    return this.costHeads.reduce(
      (acc, h) => acc + (p.values[h]?.intended_spend || 0),
      0
    );
  }

  // Used by the Popover for the "Total" column
  getTotalOugdpData(p: Project): any[] {
    const vendorMap = new Map();
    this.costHeads.forEach((head) => {
      p.values[head]?.ougdpData?.forEach((vendor: any) => {
        if (vendorMap.has(vendor.vendor_id)) {
          vendorMap.get(vendor.vendor_id).spend_amount += vendor.spend_amount;
        } else {
          vendorMap.set(vendor.vendor_id, { ...vendor });
        }
      });
    });
    return Array.from(vendorMap.values());
  }

  // Popover specific logic
  getVendorPercentage(vendor: any, totalSpend: number): number {
    if (!totalSpend || !vendor.spend_amount) return 0;
    return Math.min((vendor.spend_amount / totalSpend) * 100, 100);
  }

  getVendorColor(index: number): string {
    return this.vendorColors[index % this.vendorColors.length];
  }

  private getAllOverBudgetCostHeads(): string[] {
    const overHeads = new Set<string>();
    this.projects.forEach((project) => {
      Object.keys(project.values).forEach((costHead) => {
        const v = project.values[costHead];
        if (v?.spend > v?.budget) overHeads.add(costHead);
      });
    });
    return Array.from(overHeads);
  }

  getMenuTriggerData(p: any) {
    return { 
              project: p, 
              head: `Total ${this.displayLabels?.project} Spend`, 
              val: { spend: this.getTotalSpend(p), budget: this.getTotalBudget(p), intendedSpend: p.totalIntendedSpend, ougdpData: p.ougdpData } 
            }
  }

  setGnews() {
    const pageName = "Finance - Finance Comparitor";
    if (
      this.router.url.includes("finance") ||
      this.router.url.includes("financials")
    ) {
      this.messageService.setGnews({
        pagename: pageName,
        queryParam: { cost_heads: this.getAllOverBudgetCostHeads().join(",") },
      });
    }
  }
}
